import { Arc, Circle, Group, Line, Rect } from "react-konva";
import { PITCH_H, PITCH_W, type Dims } from "@/lib/canvas/coords";

/**
 * Soccer-pitch background, rendered directly via react-konva
 * primitives so the canvas stays a single layer and we don't have to
 * load an external SVG / image asset on boot.
 *
 * The stage (`dims`) is a 16:9 rectangle. The whole stage is filled
 * green; the pitch MARKINGS (touchlines, boxes, circle, arcs, …) are
 * drawn inside a centered sub-rectangle held at the true pitch
 * proportion (`PITCH_W : PITCH_H` ≈ 1.544 : 1). Since that's narrower
 * than 16:9, the inset is height-bound and leaves a green margin on
 * each side — so the board reads as "a scaled pitch on a green field",
 * matching the video stage's 16:9 footprint with no black letterbox
 * bars.
 *
 * The styling mirrors the broadcast-look pitch SVG: a base green with
 * ten vertical mowing stripes, white markings, penalty arcs (the "D"),
 * and corner arcs. The stripes + arcs are purely decorative — token /
 * shape coordinates are normalized to the full stage `dims`, NOT this
 * inset rectangle, so changing what's painted here never moves a token.
 *
 * The pitch is drawn landscape, attacking left-to-right (matching the
 * legacy convention used by `FORMATION_PRESETS`). All coordinates are
 * in pixels.
 *
 * Caller is responsible for the `listening: true/false` hint — this
 * component is a pure decorative layer.
 */

export type PitchBackgroundProps = {
  dims: Dims;
  /** Optional override for the field color (the base under the stripes). */
  color?: string;
  /** Optional override for line color. */
  lineColor?: string;
};

// Broadcast-look greens from the source pitch SVG.
const DEFAULT_FIELD = "#0f7a48";
const STRIPE_LIGHT = "#13864f";
const STRIPE_DARK = "#0d6b3f";
const DEFAULT_LINES = "rgba(255, 255, 255, 0.92)";
const LINE_W = 2.4;
const PITCH_RATIO = PITCH_W / PITCH_H;
const STRIPE_COUNT = 10;

export function PitchBackground(props: PitchBackgroundProps) {
  const { dims } = props;
  const fill = props.color ?? DEFAULT_FIELD;
  const stroke = props.lineColor ?? DEFAULT_LINES;
  const { width: stageW, height: stageH } = dims;

  // The pitch-markings rectangle: the largest true-proportion
  // (PITCH_W:PITCH_H) box that fits the 16:9 stage, centered. The
  // stage is wider than the pitch ratio, so it's height-bound — a
  // small green margin sits on the left + right.
  const pad = 4;
  const fitH = stageH - pad * 2;
  const fitW = Math.min(stageW - pad * 2, fitH * PITCH_RATIO);
  const pitchH = fitW / PITCH_RATIO;
  // Origin of the inset pitch within the stage.
  const ox = (stageW - fitW) / 2;
  const oy = (stageH - pitchH) / 2;

  const innerW = fitW;
  const innerH = pitchH;
  const cx = ox + innerW / 2;
  const cy = oy + innerH / 2;
  const centreR = innerH * 0.147; // matches the SVG's centre-circle radius ratio

  // Penalty boxes (SVG ratios: 0.157 W × 0.648 H of the playing area).
  const pBoxW = innerW * 0.157;
  const pBoxH = innerH * 0.648;
  const pBoxYOffset = (innerH - pBoxH) / 2;

  // 6-yard boxes (0.052 W × 0.294 H).
  const sBoxW = innerW * 0.052;
  const sBoxH = innerH * 0.294;
  const sBoxYOffset = (innerH - sBoxH) / 2;

  // Penalty spots (0.105 / 0.895 across) — also the arc centres.
  const penLeftX = ox + innerW * 0.105;
  const penRightX = ox + innerW * 0.895;
  // Penalty-arc radius equals the centre-circle radius (real-pitch ratio);
  // only the part outside the penalty box shows.
  const arcR = centreR;
  // Half-angle of the visible arc: the box edge sits `pBoxW - (penX from line)`
  // px from the spot. cos(theta) = boxEdgeDx / arcR.
  const boxEdgeDx = pBoxW - innerW * 0.105;
  const arcHalfAngle =
    arcR > 0 && Math.abs(boxEdgeDx) < arcR
      ? (Math.acos(boxEdgeDx / arcR) * 180) / Math.PI
      : 50;
  const cornerR = innerW * 0.0086; // small corner quarter-circles

  // Goal notches (decorative, just outside the touchlines).
  const goalW = innerW * 0.011;
  const goalH = innerH * 0.105;
  const goalYOffset = (innerH - goalH) / 2;

  // Stripe width within the inset pitch.
  const stripeW = innerW / STRIPE_COUNT;

  return (
    <Group listening={false}>
      {/* Green field — fills the whole 16:9 stage. */}
      <Rect x={0} y={0} width={stageW} height={stageH} fill={fill} cornerRadius={6} />

      {/* Mowing stripes, clipped to the inset pitch rectangle so they
       * don't spill onto the side margins. */}
      <Group
        clipX={ox}
        clipY={oy}
        clipWidth={innerW}
        clipHeight={innerH}
        listening={false}
      >
        {Array.from({ length: STRIPE_COUNT }, (_, i) => (
          <Rect
            key={i}
            x={ox + i * stripeW}
            y={oy}
            width={stripeW + 0.5 /* hairline overlap to avoid seams */}
            height={innerH}
            fill={i % 2 === 0 ? STRIPE_LIGHT : STRIPE_DARK}
          />
        ))}
      </Group>

      {/* ── Markings ── */}
      {/* Touchlines — the true-proportion pitch rectangle, inset. */}
      <Rect x={ox} y={oy} width={innerW} height={innerH} stroke={stroke} strokeWidth={LINE_W} />
      {/* Halfway line */}
      <Line points={[cx, oy, cx, oy + innerH]} stroke={stroke} strokeWidth={LINE_W} />
      {/* Centre circle + spot */}
      <Circle x={cx} y={cy} radius={centreR} stroke={stroke} strokeWidth={LINE_W} />
      <Circle x={cx} y={cy} radius={3.4} fill={stroke} />

      {/* Penalty boxes */}
      <Rect x={ox} y={oy + pBoxYOffset} width={pBoxW} height={pBoxH} stroke={stroke} strokeWidth={LINE_W} />
      <Rect x={ox + innerW - pBoxW} y={oy + pBoxYOffset} width={pBoxW} height={pBoxH} stroke={stroke} strokeWidth={LINE_W} />
      {/* 6-yard boxes */}
      <Rect x={ox} y={oy + sBoxYOffset} width={sBoxW} height={sBoxH} stroke={stroke} strokeWidth={LINE_W} />
      <Rect x={ox + innerW - sBoxW} y={oy + sBoxYOffset} width={sBoxW} height={sBoxH} stroke={stroke} strokeWidth={LINE_W} />

      {/* Penalty spots */}
      <Circle x={penLeftX} y={cy} radius={3.4} fill={stroke} />
      <Circle x={penRightX} y={cy} radius={3.4} fill={stroke} />

      {/* Penalty arcs (the "D") — only the portion outside each box. Konva
       * Arc angles run clockwise from 0° (east). The left arc opens to the
       * right (centred on 0°); the right arc opens left (centred on 180°). */}
      <Arc
        x={penLeftX}
        y={cy}
        innerRadius={arcR}
        outerRadius={arcR}
        angle={arcHalfAngle * 2}
        rotation={-arcHalfAngle}
        stroke={stroke}
        strokeWidth={LINE_W}
      />
      <Arc
        x={penRightX}
        y={cy}
        innerRadius={arcR}
        outerRadius={arcR}
        angle={arcHalfAngle * 2}
        rotation={180 - arcHalfAngle}
        stroke={stroke}
        strokeWidth={LINE_W}
      />

      {/* Corner arcs — a quarter circle tucked into each corner. */}
      <Arc x={ox} y={oy} innerRadius={cornerR} outerRadius={cornerR} angle={90} rotation={0} stroke={stroke} strokeWidth={LINE_W} />
      <Arc x={ox + innerW} y={oy} innerRadius={cornerR} outerRadius={cornerR} angle={90} rotation={90} stroke={stroke} strokeWidth={LINE_W} />
      <Arc x={ox + innerW} y={oy + innerH} innerRadius={cornerR} outerRadius={cornerR} angle={90} rotation={180} stroke={stroke} strokeWidth={LINE_W} />
      <Arc x={ox} y={oy + innerH} innerRadius={cornerR} outerRadius={cornerR} angle={90} rotation={270} stroke={stroke} strokeWidth={LINE_W} />

      {/* Goals (decorative notches outside the touchlines) */}
      <Rect x={ox - goalW} y={oy + goalYOffset} width={goalW} height={goalH} stroke={stroke} strokeWidth={LINE_W} />
      <Rect x={ox + innerW} y={oy + goalYOffset} width={goalW} height={goalH} stroke={stroke} strokeWidth={LINE_W} />
    </Group>
  );
}
